import { useCallback, useEffect, useRef } from 'react'
import { useVideoPlaybackStore } from '../stores/videoPlaybackStore'

/** Speeds offered by the playback-speed menu, slowest first. */
export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const
export type PlaybackRate = (typeof PLAYBACK_RATES)[number]
export const DEFAULT_PLAYBACK_RATE: PlaybackRate = 1
/** Rate applied while the viewer presses and holds the video. */
export const HOLD_PLAYBACK_RATE = 2

/** `1×`, `0.75×`, `1.5×`. */
export function formatPlaybackRate(rate: number): string {
  return `${Number(rate.toFixed(2))}×`
}

/**
 * The next menu rate after `rate` in `direction`, clamped at the ends. A rate
 * that is not in the menu steps from the nearest menu rate on that side.
 */
export function stepPlaybackRate(rate: number, direction: 1 | -1): PlaybackRate {
  if (direction > 0) {
    return PLAYBACK_RATES.find((candidate) => candidate > rate)
      ?? PLAYBACK_RATES[PLAYBACK_RATES.length - 1]
  }
  for (let i = PLAYBACK_RATES.length - 1; i >= 0; i -= 1) {
    if (PLAYBACK_RATES[i] < rate) return PLAYBACK_RATES[i]
  }
  return PLAYBACK_RATES[0]
}

export interface VideoPlaybackRateState {
  /** The viewer's chosen rate (global, shared by every player). */
  rate: number
  /** What the element actually plays at: `HOLD_PLAYBACK_RATE` while holding. */
  effectiveRate: number
  setRate: (rate: PlaybackRate) => void
}

/**
 * The single writer of `video.playbackRate` for one player.
 *
 * The chosen rate lives in `videoPlaybackStore` so every player follows it;
 * press-and-hold overrides it locally without ever writing the store, so
 * releasing always restores the viewer's choice. `video.load()` (the MP4
 * fallback, a source swap) resets `playbackRate` to 1, so the effective rate is
 * re-applied on `loadedmetadata`.
 */
export function useVideoPlaybackRate(
  video: HTMLVideoElement | null,
  holding: boolean,
): VideoPlaybackRateState {
  const rate = useVideoPlaybackStore((s) => s.playbackRate)
  const effectiveRate = holding ? HOLD_PLAYBACK_RATE : rate
  const effectiveRateRef = useRef(effectiveRate)
  effectiveRateRef.current = effectiveRate

  useEffect(() => {
    if (!video) return
    if (video.playbackRate !== effectiveRate) video.playbackRate = effectiveRate
  }, [video, effectiveRate])

  useEffect(() => {
    if (!video) return
    const reapply = () => {
      if (video.playbackRate !== effectiveRateRef.current) {
        video.playbackRate = effectiveRateRef.current
      }
    }
    video.addEventListener('loadedmetadata', reapply)
    return () => video.removeEventListener('loadedmetadata', reapply)
  }, [video])

  const setRate = useCallback((next: PlaybackRate) => {
    useVideoPlaybackStore.getState().setPlaybackRate(next)
  }, [])

  return { rate, effectiveRate, setRate }
}
